import { forwardRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import entetePool from "../../../assets/img/entete_pool.png";
import { Image } from "antd";
import BarreTop from "../../../components/docs/BarreTop";
import { DocumentFooter } from "../../../components/docs/DocumentFooter";
import { getCompagnieLogo } from "../../../hooks/useFilesHooks";

/* ─── Print + base styles ──────────────────────────────────────────────────── */
const printStyles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=DM+Mono:wght@400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  @media print {
    @page {
      size: A4 portrait;
      margin: 10mm 10mm 10mm 10mm;
    }
    html, body { margin: 0 !important; padding: 0 !important; }

    .page-break {
      page-break-before: always !important;
      break-before: page !important;
    }
    .no-break {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    .card-head { page-break-after: avoid; break-after: avoid; }

    /* ── B&W overrides ──────────────────────────────────────────────────────
       Card headers: strip fill → white bg + dark text + visible left stripe
       via box-shadow (always prints) + bottom border.
       Field values: keep the light grey bg readable (it maps to ~5% grey).
    ─────────────────────────────────────────────────────────────────────── */
    .card-head {
      background: white !important;
      color: #111 !important;
      border-bottom: 1.5px solid #333 !important;
      /* replicate the left colour stripe with a left border */
      border-left: 4px solid #333 !important;
    }
    .card-head .dot-marker {
      background: #333 !important;
    }

    /* Prevision box header */
    .prev-head {
      background: white !important;
      color: #111 !important;
      border-bottom: 1.5px solid #333 !important;
    }
    .prev-box {
      border: 2px solid #333 !important;
    }
    .prev-row {
      border-bottom: 1px solid #aaa !important;
    }

    /* Banner */
    .banner-strip {
      background: white !important;
      border: 1px solid #333 !important;
      border-left: 4px solid #333 !important;
    }

    /* Title underline */
    .title-bar {
      background: #333 !important;
    }

    /* Chip tags */
    .chip-tag {
      background: white !important;
      border: 1px solid #333 !important;
      color: #111 !important;
    }

    /* Table headers */
    .th-main {
      background: white !important;
      color: #111 !important;
      border: 1.5px solid #333 !important;
    }
    .th-sub {
      background: #f0f0f0 !important;
      color: #111 !important;
      border: 1px solid #555 !important;
    }

    /* Sketch zone */
    .sketch-zone {
      border: 1.5px dashed #555 !important;
    }

    /* Signature box */
    .sig-box {
      background: white !important;
      border: 1px dashed #333 !important;
    }

    /* Page separator top border */
    .header-border {
      border-bottom: 3px solid #111 !important;
    }
  }
`;

/* ─── Design tokens ─────────────────────────────────────────────────────────── */
const C = {
  navy: "#0f2d4a",
  blue: "#1a5fa8",
  accent: "#2a82d0",
  light: "#e8f1fa",
  muted: "#6b7f93",
  border: "#c8d8e8",
  bg: "#f3f7fb",
  white: "#ffffff",
  red: "#6b1515",
  redL: "#8b2020",
  green: "#0e3d1a",
  greenL: "#1e6b2e",
  text: "#1a2b3c",
  sub: "#4a6070",
};

/* ─── Helpers ────────────────────────────────────────────────────────────────── */
const dv = (v) => { if (v === null || v === undefined) return ""; const s = String(v).trim(); return s; };
const fd = (d) => { if (!d) return ""; try { return new Date(d).toLocaleDateString("fr-FR"); } catch { return ""; } };
const ft = (d) => { if (!d) return ""; try { return new Date(d).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const fa = (v) => { if (v === null || v === undefined || v === "") return ""; const n = Number(v); return !Number.isNaN(n) ? new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(n) : dv(v); };

/* ─── Field ──────────────────────────────────────────────────────────────────── */
const F = ({ label, value }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
    <span style={{
      fontSize: "7px", fontWeight: 700, color: C.muted,
      textTransform: "uppercase", letterSpacing: "0.5px",
    }}>
      {label}
    </span>
    {/* Line-style value — reads perfectly in B&W: just a bottom border */}
    <div style={{
      borderBottom: `1.5px solid ${C.border}`,
      padding: "2px 4px 3px",
      minHeight: "20px",
      fontSize: "9.5px",
      fontFamily: "'DM Mono', monospace",
      color: C.text,
      fontWeight: 500,
    }}>
      {value || ""}
    </div>
  </div>
);

/* ─── Card — key redesign: left-stripe + white bg header ────────────────────── */
/*
   Colour mode  : coloured left stripe (4px) + coloured text on white bg
   B&W print    : left stripe → dark grey; text → dark; readable without any fill
*/
const Card = ({ title, accent = C.navy, children }) => (
  <div className="no-break" style={{
    border: `1px solid ${C.border}`,
    borderRadius: "5px",
    overflow: "hidden",
    marginBottom: "7px",
  }}>
    <div
      className="card-head"
      style={{
        background: C.white,
        borderBottom: `1px solid ${C.border}`,
        borderLeft: `4px solid ${accent}`,
        padding: "4px 10px",
        display: "flex",
        alignItems: "center",
        gap: "6px",
      }}
    >
      {/* Coloured dot — in B&W becomes a dark dot, still clear */}
      <span
        className="dot-marker"
        style={{
          display: "inline-block",
          width: "7px", height: "7px",
          borderRadius: "50%",
          background: accent,
          flexShrink: 0,
        }}
      />
      <span style={{
        fontSize: "8.5px",
        fontWeight: 700,
        color: accent,
        letterSpacing: "0.8px",
        textTransform: "uppercase",
      }}>
        {title}
      </span>
    </div>
    <div style={{ padding: "8px 10px", background: C.white }}>
      {children}
    </div>
  </div>
);

/* ─── Grid ───────────────────────────────────────────────────────────────────── */
const G = ({ cols = 2, gap = 7, children, style = {} }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: `${gap}px`, ...style }}>
    {children}
  </div>
);

/* ─── Checkboxes ─────────────────────────────────────────────────────────────── */
const Checks = ({ options, active, accent = C.blue }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center", marginTop: "4px" }}>
    {options.map((o) => {
      const checked = dv(active) === o;
      return (
        <div key={o} style={{ display: "flex", alignItems: "center", gap: "3px" }}>
          <div style={{
            width: "11px", height: "11px",
            borderRadius: "2px", flexShrink: 0,
            border: `1.5px solid ${checked ? accent : C.border}`,
            background: checked ? accent : C.white,
            /* B&W: filled box is clearly distinct from empty box */
          }} />
          <span style={{ fontSize: "8.5px", color: C.text }}>{o}</span>
        </div>
      );
    })}
  </div>
);

const BoolCheck = ({ label, value, accentTrue = C.blue, accentFalse = C.redL }) => {
  const isTrue = value === true;
  const isFalse = value === false;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
      <div style={{ width: "11px", height: "11px", borderRadius: "2px", flexShrink: 0, border: `1.5px solid ${isTrue ? accentTrue : C.border}`, background: isTrue ? accentTrue : C.white }} />
      <span style={{ fontSize: "8.5px" }}>{label} — OUI</span>
      <div style={{ width: "11px", height: "11px", borderRadius: "2px", flexShrink: 0, border: `1.5px solid ${isFalse ? accentFalse : C.border}`, background: isFalse ? accentFalse : C.white, marginLeft: "6px" }} />
      <span style={{ fontSize: "8.5px" }}>{label} — NON</span>
    </div>
  );
};

/* ─── Page header (inline per page) ─────────────────────────────────────────── */
const PageHeader = ({ companyLogoUrl, organisme, sinistre, firstPage }) => (
  <div style={{ marginBottom: "7px" }}>
    <div
      className="header-border"
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        paddingBottom: "6px",
        borderBottom: `3px solid ${C.navy}`,
        gap: "10px",
      }}
    >
      {/* Logos */}
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        {organisme?.typeOrganisme !== "POOL" && companyLogoUrl && (
          <Image src={companyLogoUrl} style={{ height: "70px", objectFit: "contain" }} />
        )}
        <Image src={entetePool} style={{ height: "70px", objectFit: "contain" }} />
      </div>

      {/* PRÉVISIONS box — white bg, dark border in B&W */}
      {firstPage && <div
        className="prev-box"
        style={{
          border: `2px solid ${C.navy}`, borderRadius: "4px",
          overflow: "hidden", minWidth: "115px", flexShrink: 0,
        }}
      >
        <div
          className="prev-head"
          style={{
            background: C.navy, color: C.white,
            textAlign: "center", padding: "3px 6px",
            fontSize: "7.5px", fontWeight: 700,
            letterSpacing: "0.4px", lineHeight: 1.3,
            /* print-color-adjust kept intentionally; @media print overrides */
          }}
        >
          À REMPLIR PAR L'AGENCE<br />PRÉVISIONS EN COÛT TOTAL
        </div>
        {[
          ["R.C.", sinistre?.previsionRC],
          ["I.C.", sinistre?.previsionIC],
          ["D.R.", sinistre?.previsionDR],
          ["Inc.", sinistre?.previsionIncendie],
          ["Vol.", sinistre?.previsionVol],
        ].map(([lbl, v]) => (
          <div
            key={lbl}
            className="prev-row"
            style={{
              display: "grid", gridTemplateColumns: "38px 1fr",
              padding: "2px 6px", borderBottom: `1px solid ${C.border}`,
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: "8.5px", fontWeight: 700, color: C.navy }}>{lbl} =</span>
            <span style={{
              borderBottom: `1px dotted ${C.border}`,
              fontSize: "9px", fontFamily: "'DM Mono', monospace",
              minHeight: "14px", paddingLeft: "2px",
            }}>
              {fa(v)}
            </span>
          </div>
        ))}
      </div>}


    </div>
    <BarreTop />
  </div>
);

/* ─── Title strip ────────────────────────────────────────────────────────────── */
const TitleStrip = ({ text }) => (
  <div style={{ marginBottom: "7px" }}>
    <div style={{
      textAlign: "center", fontSize: "13px", fontWeight: 700, color: C.navy,
      letterSpacing: "0.8px", textTransform: "uppercase", padding: "4px 0 3px",
    }}>
      {text}
    </div>
    <div
      className="title-bar"
      style={{
        height: "3px", margin: "0 auto", width: "140px", borderRadius: "2px",
        background: `linear-gradient(90deg, ${C.navy}, ${C.accent}, ${C.light})`,
      }}
    />
  </div>
);

/* ─── Table cell style helpers ───────────────────────────────────────────────── */
const TH = (extra = {}) => ({
  padding: "5px 6px", textAlign: "center", fontWeight: 700,
  border: `1px solid ${C.border}`, fontSize: "8px", letterSpacing: "0.3px",
  background: C.navy, color: C.white,
  ...extra,
});
const ThSub = (extra = {}) => ({
  background: C.light, color: C.navy,
  padding: "4px 6px", textAlign: "center", fontWeight: 600,
  border: `1px solid ${C.border}`, fontSize: "8px",
  ...extra,
});
const TD = (extra = {}) => ({
  padding: "5px 6px", border: `1px solid ${C.border}`,
  textAlign: "center", color: C.sub,
  fontFamily: "'DM Mono', monospace", fontSize: "9px",
  ...extra,
});

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════════════════ */
const FicheDeclarationSinistre = forwardRef((props, ref) => {
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
  const organisme = props?.sinistre?.contrat?.organisme;

  useEffect(() => {
    const logoPath = organisme?.logo;
    let objectUrl;
    let isMounted = true;
    if (!logoPath) { setCompanyLogoUrl(null); return; }
    const segments = logoPath.split("/");
    const logoFileName = segments.length > 1 ? segments[1] : segments[0];
    if (!logoFileName) { setCompanyLogoUrl(null); return; }
    setCompanyLogoUrl(null);
    const fetchLogo = async () => {
      try {
        const response = await getCompagnieLogo(organisme?.code);
        if (!response.ok) throw new Error(`Logo error: ${response.status}`);
        const buffer = await response.arrayBuffer();
        if (!isMounted) return;
        objectUrl = URL.createObjectURL(new Blob([buffer]));
        setCompanyLogoUrl(objectUrl);
      } catch (e) {
        if (isMounted) setCompanyLogoUrl(null);
      }
    };
    fetchLogo();
    return () => { isMounted = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [organisme?.logo]);

  const sinistre = props?.sinistre || {};
  const contrat = sinistre?.contrat || {};
  const souscripteur = contrat?.souscripteur || {};
  const engin = contrat?.engins?.[0] || {};
  const conducteur = sinistre?.conducteur || {};
  const adversaires = sinistre?.victimes?.filter((v) => v.adversaire) || [];
  const assureVictime = sinistre?.victimes?.find((v) => !v.adversaire && v.positionVehicule) || {};
  const adversairePrincipal =
    adversaires.length === 1
      ? adversaires[0]
      : props?.sinistre?.victimes?.find((v) => v.adversairePrincipal) || adversaires[0] || {};
  const adversairePrincipalDriver = adversairePrincipal?.driver || adversairePrincipal?.personne;
  const temoins = sinistre?.temoins || [];
  const temoin0 = temoins[0] || {};

  // Traçabilité rédaction : rédacteur INITIAL (redacteurInitialNom résolu côté serveur depuis
  // created_by, repli created_by) + MODIFICATEURS successifs (redacteursDeclaration = JSON append-only).
  const redacteurInitial = sinistre?.redacteurInitialNom || sinistre?.createdBy || "";
  const modificateursRaw = (() => {
    try {
      const arr = typeof sinistre?.redacteursDeclaration === "string"
        ? JSON.parse(sinistre.redacteursDeclaration) : sinistre?.redacteursDeclaration;
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  })();
  // Repli par défaut : historique vide → dernière modification connue (updated_by / updated_at).
  const modificateurs = modificateursRaw.length
    ? modificateursRaw
    : ((sinistre?.updatedAt && sinistre.updatedAt !== sinistre?.createdAt && (sinistre?.updatedByNom || sinistre?.updatedBy))
        ? [{ nom: sinistre.updatedByNom || sinistre.updatedBy, date: sinistre.updatedAt }]
        : []);

  const headerProps = { companyLogoUrl, organisme, sinistre };

  /* ── Section label ── */
  const SectionLabel = ({ text }) => (
    <div style={{
      fontSize: "7.5px", fontWeight: 700, color: C.muted,
      textTransform: "uppercase", letterSpacing: "0.5px",
      marginBottom: "5px",
    }}>
      {text}
    </div>
  );

  return (
    <div ref={ref}>
      <style>{printStyles}</style>

      <div style={{
        fontFamily: "'DM Sans', sans-serif",
        maxWidth: "190mm",
        margin: "0 auto",
        background: C.white,
        color: C.text,
        fontSize: "11px",
        lineHeight: 1.4,
      }}>

        {/* ══════════════════════ PAGE 1 ══════════════════════ */}
        <PageHeader {...headerProps} firstPage={true} />
        <TitleStrip text="DÉCLARATION D'ACCIDENT AUTOMOBILE OU DE DEUX ROUES" />

        {/* Banner */}
        <div
          className="banner-strip"
          style={{
            display: "flex", flexWrap: "wrap", gap: "6px",
            justifyContent: "space-between",
            padding: "5px 10px", marginBottom: "7px",
            background: C.light,
            borderLeft: `3px solid ${C.accent}`,
            borderRadius: "3px", fontSize: "9px",
          }}
        >
          {/* <span><strong>Date d'envoi :</strong> {fd(sinistre?.dateEnvoi) || "_______________"}</span> */}
          <span><strong>N° de Police :</strong> {dv(contrat?.numeroPoliceP) || "_______________"}</span>
          <span><strong>Sinistre N° :</strong> {dv(sinistre?.numSinistre) || "_______________"}</span>
          <span><strong>Date de l'accident :</strong> {fd(sinistre?.dateSinistre) || "_______________"}</span>
        </div>

        {/* Agence / Police */}
        <G cols={2} gap={7} style={{ marginBottom: "7px" }}>
          <F label="Agence" value={dv(contrat?.agence)} />
          <F label="Date d'envoi " value={fd(sinistre?.dateEnvoi)} />
        </G>

        {/* ── ASSURÉ / ADVERSAIRE ── */}
        <G cols={2} gap={7} style={{ marginBottom: "7px" }}>
          <Card title="Assuré" accent={C.navy}>
            <G cols={2} gap={6}>
              <F label="Nom" value={`${dv(souscripteur?.nom)} ${dv(souscripteur?.prenom)}`} />
              <F label="Profession" value={dv(souscripteur?.profession)} />
            </G>
            <div style={{ marginTop: "5px" }}><F label="Adresse" value={dv(souscripteur?.adresse)} /></div>
            <div style={{ marginTop: "5px" }}><F label="Téléphone" value={dv(souscripteur?.contact?.[0])} /></div>
          </Card>

          <Card title="Adversaire" accent={C.redL}>
            <G cols={2} gap={6}>
              <F label="Nom" value={`${dv(adversairePrincipal?.personne?.nom)} ${dv(adversairePrincipal?.personne?.prenom)}`} />
              <F label="Profession" value={dv(adversairePrincipal?.personne?.profession)} />
            </G>
            <div style={{ marginTop: "5px" }}><F label="Adresse" value={dv(adversairePrincipal?.personne?.adresse)} /></div>
            <div style={{ marginTop: "5px" }}><F label="Assuré à la Sté" value={dv(adversairePrincipal?.societeAssurance)} /></div>
          </Card>
        </G>

        {/* ── VÉHICULES ── */}
        <G cols={2} gap={7} style={{ marginBottom: "7px" }}>
          <Card title="Véhicule Assuré" accent={C.navy}>
            <G cols={3} gap={6}>
              <F label="Marque" value={dv(engin?.marque)} />
              <F label="Force" value={dv(engin?.force)} />
              <F label="Usage" value={dv(engin?.usage)} />
            </G>
            <G cols={3} gap={6} style={{ marginTop: "5px" }}>
              <F label="Immatriculation" value={dv(engin?.immatriculation)} />
              <F label="N° de Série" value={dv(engin?.numeroSerie)} />
              <F label="Type" value={dv(engin?.type)} />
            </G>
            <G cols={3} gap={6} style={{ marginTop: "5px" }}>
              <F label="D. vis. technique" value={fd(engin?.dateVisiteTechnique)} />
              <F label="Transportés (gratuit)" value={dv(sinistre?.nbPersonnesGratuit)} />
              <F label="Transportés (onéreux)" value={dv(sinistre?.nbPersonnesOnereux)} />
            </G>
          </Card>

          <Card title="Véhicule Adversaire" accent={C.redL}>
            <G cols={3} gap={6}>
              <F label="Marque" value={dv(adversairePrincipal?.engin?.marque)} />
              <F label="Force" value={dv(adversairePrincipal?.engin?.puissance)} />
              <F label="Usage" value={dv(adversairePrincipal?.engin?.usage)} />
            </G>
            <div style={{ marginTop: "5px" }}>
              <F label="Immatriculation" value={dv(adversairePrincipal?.engin?.immatriculation)} />
            </div>
            <div style={{ marginTop: "5px" }}>
              <F label="Nombre de personnes transportées" value={dv(adversairePrincipal?.nbPersonnesTransportees)} />
            </div>
          </Card>
        </G>

        {/* ── CONDUCTEURS ── */}
        <G cols={2} gap={7} style={{ marginBottom: "7px" }}>
          <Card title="Conducteur Assuré" accent={C.navy}>
            <G cols={2} gap={6}>
              <F label="Nom" value={dv(conducteur?.nom || souscripteur?.nom)} />
              <F label="Date de naissance" value={fd(conducteur?.dateNaissance)} />
            </G>
            <G cols={3} gap={6} style={{ marginTop: "5px" }}>
              <F label="N° Permis" value={dv(conducteur?.numeroPermis)} />
              <F label="Catégorie" value={dv(conducteur?.categoriePermis)} />
              <F label="Lieu délivrance" value={dv(conducteur?.lieuDelivrance)} />
            </G>
            <div style={{ marginTop: "5px" }}>
              <F label="Date de délivrance" value={fd(conducteur?.dateDelivrance)} />
            </div>
            <div style={{ marginTop: "6px" }}>
              <SectionLabel text="Qualité du conducteur" />
              <Checks
                options={["Propriétaire", "Préposé / Salarié", "Emprunteur", "Bénévole", "Locataire"]}
                active={conducteur?.qualite}
                accent={C.blue}
              />
            </div>
          </Card>

          <Card title="Conducteur Adversaire" accent={C.redL}>
            <F label="Nom" value={dv(adversairePrincipalDriver?.nom)} />
            <G cols={2} gap={6} style={{ marginTop: "5px" }}>
              <F label="N° Permis" value={dv(adversairePrincipalDriver?.numeroPermis)} />
              <F label="Catégorie" value={dv(adversairePrincipalDriver?.categoriePermis)} />
            </G>
            <G cols={2} gap={6} style={{ marginTop: "5px" }}>
              <F label="Lieu de délivrance" value={dv(adversairePrincipalDriver?.lieuDelivrance)} />
              <F label="Date de délivrance" value={fd(adversairePrincipalDriver?.dateDelivrance)} />
            </G>
            <div style={{ marginTop: "6px" }}>
              <SectionLabel text="Qualité" />
              <Checks
                options={["Préposé / Salarié", "Emprunteur", "Propriétaire"]}
                active={adversairePrincipalDriver?.qualite}
                accent={C.redL}
              />
            </div>
          </Card>
        </G>

        {/* ── DOMMAGES ── */}
        <Card title="Évaluation des Dommages" accent={C.navy}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
            <thead>
              <tr>
                <th className="th-main" style={TH({})} colSpan="5">Dommages Corporels</th>
                <th className="th-main" style={TH({ background: "#4a3010" })} colSpan="2">Dommages Matériels</th>
              </tr>
              <tr>
                {["Blessé(s) léger(s)", "Blessé(s) moyen(s)", "Blessé(s) grave(s)", "Mort(s)", "Resp. (%)"].map((h) => (
                  <th key={h} className="th-sub" style={ThSub({})}>{h}</th>
                ))}
                <th className="th-sub" style={ThSub({})}>Assuré</th>
                <th className="th-sub" style={ThSub({})}>Adversaire</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={TD({})}>{sinistre?.nombreBlesseLeger || "0"}</td>
                <td style={TD({})}>{sinistre?.nombreBlesseMoyen || "0"}</td>
                <td style={TD({})}>{sinistre?.nombreBlesseGrave || "0"}</td>
                <td style={TD({})}>{sinistre?.nombreDeces || "0"}</td>
                <td style={TD({ fontWeight: 700, color: C.navy })}>{sinistre?.tauxResponsabilite ?? "N/A"}{sinistre?.tauxResponsabilite !== undefined ? "%" : ""}</td>
                <td style={TD({ textAlign: "left" })}>{dv(sinistre?.typeDegats)}</td>
                <td style={TD({ textAlign: "left" })}>{dv(adversairePrincipal?.typeDegats)}</td>
              </tr>
            </tbody>
          </table>
          <G cols={2} gap={7} style={{ marginTop: "6px" }}>
            <F label="Adresse du véhicule assuré" value={dv(assureVictime?.positionVehicule)} />
            <F label="Adresse du véhicule adverse" value={dv(adversairePrincipal?.positionVehicule)} />
          </G>
        </Card>

        {/* ── GARANTIES ── */}
        <Card title="Garanties & Évaluation" accent={C.navy}>
          <G cols={2} gap={7}>
            <div>
              <SectionLabel text="Garantie(s) concernée(s)" />
              <div style={{ display: "flex", flexWrap: "wrap", gap: "3px", minHeight: "20px" }}>
                {sinistre?.garanties?.length
                  ? sinistre.garanties.map((g) => (
                    <span
                      key={g}
                      className="chip-tag"
                      style={{
                        display: "inline-flex", alignItems: "center",
                        padding: "1px 7px",
                        background: C.light,
                        border: `1px solid ${C.accent}`,
                        borderRadius: "20px",
                        fontSize: "8.5px", color: C.blue, fontWeight: 600,
                      }}
                    >
                      {g?.startsWith("RC") ? "RC" : g}
                    </span>
                  ))
                  : <span style={{ fontSize: "9px", color: C.muted }}>—</span>}
              </div>
            </div>
            <F label="Évaluation d'ouverture" value={fa(sinistre?.evaluationOuverture)} />
          </G>
        </Card>

        <DocumentFooter />

        {/* ══════════════════════ PAGE 2 ══════════════════════ */}
        <div className="page-break" />

        <PageHeader {...headerProps} />
        <TitleStrip text="Accident — Circonstances & Cadre Agence" />

        {/* ── DÉTAILS ACCIDENT ── */}
        <Card title="Détails de l'Accident" accent={C.navy}>
          <G cols={3} gap={7}>
            <F label="Lieu" value={dv(sinistre?.lieu)} />
            <F label="Date du sinistre" value={fd(sinistre?.dateSinistre)} />
            <F label="Heure" value={ft(sinistre?.dateSinistre)} />
          </G>

          <div style={{ marginTop: "8px" }}>
            <SectionLabel text="Mouvements des véhicules" />
            {/* Two-column movement block — clear divider between assuré and adverse */}
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1px 1fr", gap: "0",
            }}>
              {/* Assuré column */}
              <div style={{ paddingRight: "10px" }}>
                <div style={{
                  fontSize: "9px", fontWeight: 700, color: C.blue,
                  textTransform: "uppercase", letterSpacing: "0.4px",
                  borderLeft: `3px solid ${C.blue}`, paddingLeft: "5px",
                  marginBottom: "5px",
                }}>
                  ▶ Véhicule Assuré
                </div>
                <G cols={2} gap={6}>
                  <F label="Venait de" value={dv(sinistre?.provenanceAssure)} />
                  <F label="Se dirigeait vers" value={dv(sinistre?.destinationAssure)} />
                </G>
                <div style={{ marginTop: "5px" }}>
                  <F label="À une vitesse de (km/h)" value={dv(sinistre?.vitesseAssure)} />
                </div>
              </div>

              {/* Visual divider */}
              <div style={{ borderLeft: `1px dashed ${C.border}`, margin: "0 0px" }} />

              {/* Adverse column */}
              <div style={{ paddingLeft: "10px" }}>
                <div style={{
                  fontSize: "9px", fontWeight: 700, color: C.redL,
                  textTransform: "uppercase", letterSpacing: "0.4px",
                  borderLeft: `3px solid ${C.redL}`, paddingLeft: "5px",
                  marginBottom: "5px",
                }}>
                  ▶ Véhicule Adverse
                </div>
                <G cols={2} gap={6}>
                  <F label="Venait de" value={dv(adversairePrincipal?.provenance)} />
                  <F label="Se dirigeait vers" value={dv(adversairePrincipal?.destination)} />
                </G>
                <div style={{ marginTop: "5px" }}>
                  <F label="À une vitesse de (km/h)" value={dv(adversairePrincipal?.vitesse)} />
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* ── TÉMOINS ── */}
        <Card title="Témoins" accent={C.navy}>
          <G cols={3} gap={7}>
            <F label="Nom du témoin" value={dv(temoin0?.nom)} />
            <F label="Adresse" value={dv(temoin0?.adresse)} />
            <F label="Qualité" value={dv(temoin0?.qualite)} />
          </G>
          {/* Afficher l'Entité de constat */}
          <div style={{ marginTop: "7px" }} >
            <F label="Entité de constat" value={dv(sinistre?.entiteConstat?.nom)} />
          </div>
          <div style={{ marginTop: "7px" }}>
            <SectionLabel text="Rapport de police / gendarmerie" />
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginTop: "3px" }}>
              <BoolCheck label="Rapport Police" value={sinistre?.rapportPolice} accentTrue={C.blue} accentFalse={C.redL} />
              <BoolCheck label="Rapport Gendarmerie" value={sinistre?.rapportGendarmerie} accentTrue={C.blue} accentFalse={C.redL} />
            </div>
          </div>
        </Card>

        {/* ── CIRCONSTANCES ── */}
        <Card title="Circonstances de l'Accident" accent={C.navy}>
          <div style={{
            minHeight: "58px", padding: "7px 9px",
            border: `1px solid ${C.border}`, borderRadius: "3px",
            borderLeft: `3px solid ${C.border}`,
            fontSize: "10px", lineHeight: 1.7, color: C.text, whiteSpace: "pre-wrap",
          }}>
            {dv(sinistre?.circonstance) || (
              <span style={{ color: C.muted, fontStyle: "italic", fontSize: "9px" }}>
                Aucune circonstance renseignée.
              </span>
            )}
          </div>
        </Card>

        {/* ── CROQUIS + OBSERVATIONS ── */}
        <G cols={2} gap={7} style={{ marginBottom: "7px" }}>
          <Card title="Croquis de l'Accident" accent={C.navy}>
            <div
              className="sketch-zone"
              style={{
                minHeight: "110px",
                border: `1.5px dashed ${C.border}`,
                borderRadius: "4px",
                background: "#fafcff",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: C.muted, fontSize: "9px", fontStyle: "italic",
              }}
            >
              Espace réservé au croquis
            </div>
          </Card>

          <Card title="Observations de l'Agent" accent={C.greenL}>
            <div style={{
              minHeight: "110px", padding: "7px 9px",
              border: `1px solid ${C.border}`, borderRadius: "3px",
              borderLeft: `3px solid ${C.greenL}`,
              fontSize: "11px", lineHeight: 1.7, color: C.text, whiteSpace: "pre-wrap",
            }}>
              {dv(sinistre?.observationsAgent)}
            </div>
          </Card>
        </G>

        {/* ── SIGNATURE ── */}
        <Card title="Signature" accent={C.navy}>
          <G cols={3} gap={7}>
            <F label="Fait à" value="LOMÉ" />
            <F label="Le" value={fd(sinistre?.dateDeclaration)} />
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span style={{ fontSize: "8px", fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Signature de l'Assuré
              </span>
              <div
                className="sig-box"
                style={{
                  minHeight: "38px", padding: "4px 7px",
                  background: "#fffdf0",
                  border: `1px dashed ${C.border}`,
                  borderRadius: "3px",
                  fontSize: "10.5px", fontFamily: "'DM Mono', monospace",
                }}
              >
                {dv(sinistre?.signatureAssure)}
              </div>
            </div>
          </G>
        </Card>

        {/* ── CADRE AGENCE ── */}
        <Card title="Cadre Réservé à l'Agence" accent={C.greenL}>
          {/* Dernière prime */}
          <div style={{ marginBottom: "8px" }}>
            <SectionLabel text="Dernière prime" />
            <div style={{ display: "flex", gap: "14px", alignItems: "center", flexWrap: "wrap" }}>
              {[["Payée", "payee"], ["Impayée", "impayee"]].map(([lbl, key]) => {
                const checked = sinistre?.statutDernierePrime === key;
                return (
                  <div key={key} style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                    <div style={{
                      width: "11px", height: "11px", borderRadius: "2px",
                      border: `1.5px solid ${checked ? C.greenL : C.border}`,
                      background: checked ? C.greenL : C.white,
                    }} />
                    <span style={{ fontSize: "9px" }}>{lbl}</span>
                  </div>
                );
              })}
              <span style={{ fontSize: "9px", color: C.sub }}>
                le :{" "}
                <strong style={{ fontFamily: "'DM Mono', monospace" }}>
                  {fd(sinistre?.dateDernierePrime) || "____________________"}
                </strong>
              </span>
            </div>
          </div>

          {/* Franchise table */}
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9.5px" }}>
            <thead>
              <tr>
                <th className="th-main" style={TH({ background: C.green, width: "120px" })} rowSpan="2">Franchise / Garantie</th>
                <th className="th-main" style={TH({ background: C.green })}>R.C.</th>
                <th className="th-main" style={TH({ background: C.green })}>D.R.</th>
                <th className="th-main" style={TH({ background: C.green })}>Incendie</th>
                <th className="th-main" style={TH({ background: C.green })}>Vol</th>
                <th className="th-main" style={TH({ background: C.green })} colSpan="2">Tiers transportés — Gratuit</th>
                <th className="th-main" style={TH({ background: C.green })} colSpan="2">Tiers transportés — Payant</th>
              </tr>
              <tr>
                {["", "", "", "", "Cabine", "Hors Cabine", "Cabine", "Hors Cabine"].map((h, i) => (
                  <th key={i} className="th-sub" style={ThSub({})}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={TD({ textAlign: "left", fontWeight: 600, background: C.bg, fontSize: "8.5px" })}>
                  Franchise garantie
                </td>
                {[
                  sinistre?.franchiseRC,
                  sinistre?.franchiseTierce,
                  sinistre?.franchiseIncendie,
                  sinistre?.franchiseVol,
                  sinistre?.tiersCabineGratuit,
                  sinistre?.tiersHorsCabineGratuit,
                  sinistre?.tiersCabinePayant,
                  sinistre?.tiersHorsCabinePayant,
                ].map((v, i) => (
                  <td key={i} style={TD({ minWidth: "38px", height: "28px" })}>{fa(v)}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </Card>


        <DocumentFooter />

        {/* ══════════════════════ PAGE 3 ══════════════════════ */}
        <div className="page-break" />

        <PageHeader {...headerProps} />
        <TitleStrip text="Récapitulatif des Parties Impliquées" />

        {/* ── VICTIMES VÉHICULE ASSURÉ ── */}
        <Card title="Victimes dans le véhicule assuré" accent={C.navy}>
          {(() => {
            const victimesAssure = sinistre?.victimes?.filter(
              (v) => !v.adversaire && (v.positionVehicule || v.nom || v.personne)
            ) || [];
            if (!victimesAssure.length) {
              return (
                <div style={{ textAlign: "center", padding: "12px", color: C.muted, fontStyle: "italic", fontSize: "9px", border: `1.5px dashed ${C.border}`, borderRadius: "4px" }}>
                  Aucune victime renseignée dans le véhicule assuré.
                </div>
              );
            }
            return (
              <>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px" }}>
                  <thead>
                    <tr>
                      {["Nom / Prénom", "Position", "Blessures / Lésions", "Transport", "Décès"].map((h) => (
                        <th key={h} className="th-main" style={TH({})}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {victimesAssure.map((v, i) => {
                      const p = v.personne || v;
                      return (
                        <tr key={i}>
                          <td style={TD({ textAlign: "left" })}>{`${dv(p?.nom)} ${dv(p?.prenom)}`}</td>
                          <td style={TD({})}>{dv(v.positionVehicule)}</td>
                          <td style={TD({})}>{dv(v.typeBlessure || v.descriptionDegatsVictime || v.precisionBlessure)}</td>
                          <td style={TD({})}>{dv(v.typeTransport)}</td>
                          <td style={TD({ fontWeight: 700, color: v.deces ? C.redL : C.greenL })}>
                            {v.deces ? "Oui" : "Non"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <G cols={2} gap={7} style={{ marginTop: "6px" }}>
                  <F label="Nb transportés (gratuit)" value={dv(sinistre?.nbPersonnesGratuit)} />
                  <F label="Nb transportés (onéreux)" value={dv(sinistre?.nbPersonnesOnereux)} />
                </G>
              </>
            );
          })()}
        </Card>

        {/* ── ADVERSAIRES + LEURS VICTIMES ── */}
        {adversaires.length === 0 ? (
          <Card title="Adversaires" accent={C.redL}>
            <div style={{ textAlign: "center", padding: "12px", color: C.muted, fontStyle: "italic", fontSize: "9px", border: `1.5px dashed ${C.border}`, borderRadius: "4px" }}>
              Aucun adversaire renseigné.
            </div>
          </Card>
        ) : (
          adversaires.map((adv, idx) => {
            const driver = adv.driver || adv.personne || {};
            const victimesAdv = sinistre?.victimes?.filter(
              (v) => v.adversaire && (v.vehiculeAdversaireIndex === idx || (idx === 0 && !v.vehiculeAdversaireIndex))
            ) || [];

            return (
              <Card
                key={idx}
                title={`Adversaire ${idx + 1} — Véhicule & Conducteur`}
                accent={C.redL}
              >
                {/* Badge + résumé rapide */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                  <span style={{
                    background: C.redL, color: C.white,
                    fontSize: "7.5px", fontWeight: 700,
                    padding: "2px 8px", borderRadius: "10px", letterSpacing: "0.5px",
                  }}>
                    ADV. {idx + 1}
                  </span>
                  <span style={{ fontSize: "9px", fontWeight: 600 }}>
                    {`${dv(adv.personne?.nom)} ${dv(adv.personne?.prenom)}`}
                    {adv.engin?.immatriculation ? ` — ${dv(adv.engin?.immatriculation)}` : ""}
                  </span>
                </div>

                {/* Infos adversaire */}
                <G cols={3} gap={6}>
                  <F label="Nom complet"
                    value={`${dv(adv.personne?.nom)} ${dv(adv.personne?.prenom)}`} />
                  <F label="Société d'assurance" value={dv(adv.societeAssurance)} />
                  <F label="Immatriculation" value={dv(adv.engin?.immatriculation)} />
                </G>
                <G cols={3} gap={6} style={{ marginTop: "5px" }}>
                  <F label="Marque / Modèle" value={`${dv(adv.engin?.marque)} ${dv(adv.engin?.modele)}`} />
                  <F label="Usage" value={dv(adv.engin?.usage)} />
                  <F label="Dommages véhicule" value={dv(adv.typeDegats)} />
                </G>
                <G cols={3} gap={6} style={{ marginTop: "5px" }}>
                  <F label="N° permis conducteur" value={dv(driver?.numeroPermis)} />
                  <F label="Catégorie" value={dv(driver?.categoriePermis)} />
                  <F label="Qualité conducteur" value={dv(driver?.qualite)} />
                </G>

                {/* Victimes dans ce véhicule adverse */}
                <div style={{
                  fontSize: "7.5px", fontWeight: 700, color: C.muted,
                  textTransform: "uppercase", letterSpacing: "0.5px",
                  marginTop: "8px", marginBottom: "5px",
                  borderLeft: `3px solid ${C.border}`, paddingLeft: "5px",
                }}>
                  Victimes dans le véhicule adverse n°{idx + 1}
                </div>

                {victimesAdv.length === 0 ? (
                  <div style={{
                    textAlign: "center", padding: "8px", color: C.muted,
                    fontStyle: "italic", fontSize: "9px",
                    border: `1.5px dashed ${C.border}`, borderRadius: "4px",
                  }}>
                    Aucune victime renseignée pour cet adversaire.
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px" }}>
                    <thead>
                      <tr>
                        {["Nom / Prénom", "Position", "Blessures / Lésions", "Décès"].map((h) => (
                          <th key={h} className="th-main" style={TH({ background: C.redL })}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {victimesAdv.map((v, vi) => {
                        const p = v.personne || v;
                        return (
                          <tr key={vi}>
                            <td style={TD({ textAlign: "left" })}>{`${dv(p?.nom)} ${dv(p?.prenom)}`}</td>
                            <td style={TD({})}>{dv(v.positionVehicule)}</td>
                            <td style={TD({})}>{dv(v.typeBlessure || v.descriptionDegatsVictime || v.precisionBlessure)}</td>
                            <td style={TD({ fontWeight: 700, color: v.deces ? C.redL : C.greenL })}>
                              {v.deces ? "Oui" : "Non"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                <div style={{ marginTop: "5px" }}>
                  <F label="Nb de personnes transportées" value={dv(adv.nbPersonnesTransportees)} />
                </div>
              </Card>
            );
          })
        )}

        {/* Traçabilité rédaction : rédacteur initial + modificateurs successifs. */}
        <div style={{
          marginTop: "8px", padding: "6px 8px", border: `1px solid ${C.border}`,
          borderRadius: "4px", fontSize: "8px", color: C.muted,
        }}>
          <span style={{ fontWeight: 700, color: C.navy }}>Déclaration prise par : </span>
          {dv(redacteurInitial) || "—"}{sinistre?.createdAt ? ` — le ${fd(sinistre.createdAt)}` : ""}
          {modificateurs.length > 0 && (
            <span>{"  ·  "}
              <span style={{ fontWeight: 700, color: C.navy }}>Modifiée par : </span>
              {modificateurs
                .map((m) => `${dv(m?.nom || m?.codeId) || "—"}${m?.date ? ` (${fd(m.date)})` : ""}`)
                .join("  ;  ")}
            </span>
          )}
        </div>

        <DocumentFooter />
      </div>
    </div>
  );
});

FicheDeclarationSinistre.propTypes = {
  sinistre: PropTypes.object,
};

export default FicheDeclarationSinistre;